import { useReducer, useCallback } from 'react';
import { AdminPaperTranslation, AdminPrinterTranslation, TranslationStatus, AdminTranslationLocale } from '@/services/adminService';

export type EntityType = 'paper' | 'printer';
export type TranslationEntity = AdminPaperTranslation | AdminPrinterTranslation;

export type TranslationFormValue = {
  readonly description: string;
  readonly status: TranslationStatus;
};

export type TranslationFormState = Record<string, TranslationFormValue>;

export const buildFormKey = (entityType: EntityType, entityId: string, locale: string): string =>
  `${entityType}:${entityId}:${locale}`;

interface State {
  isLoading: boolean;
  loadError: string | null;
  paperTranslations: AdminPaperTranslation[];
  printerTranslations: AdminPrinterTranslation[];
  paperFormState: TranslationFormState;
  printerFormState: TranslationFormState;
  dirtyKeys: Record<string, boolean>;
  savingKeys: Record<string, boolean>;
}

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { papers: AdminPaperTranslation[]; printers: AdminPrinterTranslation[] } }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'FORM_CHANGE'; payload: { entityType: EntityType; entityId: string; locale: string; value: Partial<TranslationFormValue> } }
  | { type: 'RESET_FORM'; payload: { entityType: EntityType; entityId: string; locale: string } }
  | { type: 'SET_SAVING'; payload: { key: string; isSaving: boolean } }
  | { type: 'APPLY_UPDATE'; payload: { entityType: EntityType; entityId: string; locale: string; translation: AdminTranslationLocale } };

const buildInitialFormState = (entities: ReadonlyArray<TranslationEntity>, entityType: EntityType): TranslationFormState => {
  const state: TranslationFormState = {};
  for (const entity of entities) {
    for (const translation of entity.translations) {
      const key = buildFormKey(entityType, entity.id, translation.locale);
      state[key] = {
        description: translation.description ?? '',
        status: translation.status ?? 'mt_seeded',
      };
    }
  }
  return state;
};

const initialState: State = {
  isLoading: false,
  loadError: null,
  paperTranslations: [],
  printerTranslations: [],
  paperFormState: {},
  printerFormState: {},
  dirtyKeys: {},
  savingKeys: {},
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, loadError: null };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        paperTranslations: action.payload.papers,
        printerTranslations: action.payload.printers,
        paperFormState: buildInitialFormState(action.payload.papers, 'paper'),
        printerFormState: buildInitialFormState(action.payload.printers, 'printer'),
        dirtyKeys: {},
        savingKeys: {},
      };
    case 'FETCH_ERROR':
      return { ...state, isLoading: false, loadError: action.payload };
    case 'FORM_CHANGE': {
      const { entityType, entityId, locale, value } = action.payload;
      const key = buildFormKey(entityType, entityId, locale);
      const formState = entityType === 'paper' ? state.paperFormState : state.printerFormState;
      const updatedFormState = {
        ...formState,
        [key]: { ...formState[key], ...value },
      };
      return {
        ...state,
        ...(entityType === 'paper' ? { paperFormState: updatedFormState } : { printerFormState: updatedFormState }),
        dirtyKeys: { ...state.dirtyKeys, [key]: true },
      };
    }
    case 'RESET_FORM': {
        const { entityType, entityId, locale } = action.payload;
        const key = buildFormKey(entityType, entityId, locale);
        const sourceEntities = entityType === 'paper' ? state.paperTranslations : state.printerTranslations;
        const entity = sourceEntities.find((item) => item.id === entityId);
        const translation = entity?.translations.find((item) => item.locale === locale);
        const formState = entityType === 'paper' ? state.paperFormState : state.printerFormState;
        const updatedFormState = {
            ...formState,
            [key]: {
                description: translation?.description ?? '',
                status: translation?.status ?? 'mt_seeded',
            },
        };
        const { [key]: _, ...restDirtyKeys } = state.dirtyKeys;
        return {
            ...state,
            ...(entityType === 'paper' ? { paperFormState: updatedFormState } : { printerFormState: updatedFormState }),
            dirtyKeys: restDirtyKeys,
        };
    }
    case 'SET_SAVING': {
      const { key, isSaving } = action.payload;
      const newSavingKeys = { ...state.savingKeys };
      if (isSaving) {
        newSavingKeys[key] = true;
      } else {
        delete newSavingKeys[key];
      }
      return { ...state, savingKeys: newSavingKeys };
    }
    case 'APPLY_UPDATE': {
      const { entityType, entityId, locale, translation } = action.payload;
      const key = buildFormKey(entityType, entityId, locale);
      const updater = <T extends TranslationEntity>(entities: ReadonlyArray<T>): T[] =>
        entities.map((entity) => {
          if (entity.id !== entityId) return entity;
          const newTranslations = entity.translations.map((t) => (t.locale === locale ? translation : t));
          if (!newTranslations.some(t => t.locale === locale)) {
            newTranslations.push(translation);
          }
          return { ...entity, translations: newTranslations } as T;
        });

      const { [key]: _, ...restDirtyKeys } = state.dirtyKeys;
      const formState = entityType === 'paper' ? state.paperFormState : state.printerFormState;

      return {
        ...state,
        ...(entityType === 'paper' ? { paperTranslations: updater(state.paperTranslations) } : { printerTranslations: updater(state.printerTranslations) }),
        ...(entityType === 'paper' ? { paperFormState: { ...formState, [key]: { description: translation.description ?? '', status: translation.status ?? 'mt_seeded' } } } : { printerFormState: { ...formState, [key]: { description: translation.description ?? '', status: translation.status ?? 'mt_seeded' } } }),
        dirtyKeys: restDirtyKeys,
      };
    }
    default:
      return state;
  }
};

export const useAdminTranslations = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleDescriptionChange = useCallback((entityType: EntityType, entityId: string, locale: string, description: string) => {
    dispatch({ type: 'FORM_CHANGE', payload: { entityType, entityId, locale, value: { description } } });
  }, []);

  const handleStatusChange = useCallback((entityType: EntityType, entityId: string, locale: string, status: TranslationStatus) => {
    dispatch({ type: 'FORM_CHANGE', payload: { entityType, entityId, locale, value: { status } } });
  }, []);

  const handleReset = useCallback((entityType: EntityType, entityId: string, locale: string) => {
    dispatch({ type: 'RESET_FORM', payload: { entityType, entityId, locale } });
  }, []);

  return { state, dispatch, handleDescriptionChange, handleStatusChange, handleReset };
};
